import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { uploadMediaFile } from '../../../../actions/mediaUpload';
import ContentMedia from '../../../../components/ContentMedia';
import AdvancedSettingsModal from './AdvancedSettingsModal';
import ImageUploadSection from './ImageUploadSection';
import SettingsRow from './SettingsRow';
import GenerateFooter from './GenerateFooter';
import {
  PRIMARY,
  formatParamLabel,
  classifyMediaParam,
  isMediaName,
} from '../utils/parameterUtils';

/**
 * DefaultPresentation - Standard form layout for models/workflows
 * 
 * Layout (from screenshot):
 * - Header with back button and title
 * - Cover image with "Change" button
 * - Warning message (if any)
 * - Model selector row
 * - Image upload area
 * - Prompt text area
 * - Dimension and number of images selectors
 * - Generate button footer
 */
const CHAR_LIMIT = 2500;
const CHAR_WARNING = 2200;

export default function DefaultPresentation({
  item,
  itemType,
  examples,
  generating,
  jobStatus,
  error,
  onGenerate,
  onBack,
  onChangeModel,
  colors,
}) {
  // State for inputs (mirrors ControlBar logic)
  const [textInputs, setTextInputs] = useState({});
  const [uploadedImages, setUploadedImages] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [settings, setSettings] = useState({
    width: null,
    height: null,
    num_images: 1,
  });
  
  // Advanced settings modal state
  const [advancedSettingsOpen, setAdvancedSettingsOpen] = useState(false);
  
  const fileInputRefs = useRef({});

  // Apply defaults when item loads
  useEffect(() => {
    if (!item?.parameters) return;
    
    const newTextDefaults = {};
    
    item.parameters.forEach(p => {
      if (p.is_private || Number(p.is_private) === 1) return;
      
      const name = p.parameter_name.toLowerCase();
      const isImageField = isMediaName(name);

      if (p.data_type === 'string' && !isImageField) {
        if (p.default_value !== undefined && p.default_value !== null && p.default_value !== '') {
          newTextDefaults[p.parameter_name] = p.default_value;
        }
      }
    });
    
    if (Object.keys(newTextDefaults).length > 0) {
      setTextInputs(prev => ({ ...prev, ...newTextDefaults }));
    }
    
    // Set default dimensions from item resolution
    if (item.resolution?.dimension_presets?.length > 0) {
      const defaultPreset = item.resolution.dimension_presets[0];
      setSettings(prev => ({
        ...prev,
        width: defaultPreset.width,
        height: defaultPreset.height,
      }));
    }
  }, [item]);

  // Parse parameters into categories (same logic as ControlBar)
  const { imageParams, textParams, selectParams, dimensionConfig } = useMemo(() => {
    if (!item?.parameters) return { imageParams: [], textParams: [], selectParams: [], dimensionConfig: null };
    
    const params = item.parameters.filter(p => !p.is_private && Number(p.is_private) !== 1);
    
    // Media parameters (image / video / audio)
    const imageParamsList = [];
    params.forEach(p => {
      const m = classifyMediaParam(p);
      if (!m) return;
      imageParamsList.push({
        paramName: p.parameter_name,
        title: formatParamLabel(p.parameter_name, p.parameter_title),
        kind: m.kind,
        type: m.isArray ? 'array' : 'single',
        min: m.min,
        max: m.max,
        required: m.required,
        description: p.description
      });
    });

    // Text parameters (strings without allowed_values)
    const textParamsList = params.filter(p => {
      const name = p.parameter_name.toLowerCase();
      return p.data_type === 'string' && !isMediaName(name) && !p.allowed_values;
    });
    
    // Dimension config (structured format)
    const dp = item.resolution?.dimension_presets;
    const dimConfig = (dp?.ratios && dp?.lookup) ? dp : null;
    
    // Select parameters (strings WITH allowed_values)
    // Exclude aspect_ratio/resolution when dimension config is active
    const selectParamsList = params.filter(p => {
      const name = p.parameter_name.toLowerCase();
      if (p.data_type !== 'string' || !p.allowed_values) return false;
      if (isMediaName(name) || name.includes('webhook')) return false;
      if (dimConfig && (name === 'aspect_ratio' || name === 'resolution')) return false;
      return true;
    });
    
    return {
      imageParams: imageParamsList,
      textParams: textParamsList,
      selectParams: selectParamsList,
      dimensionConfig: dimConfig,
    };
  }, [item]);

  // Parse condition_rule from parameter (handles string or object)
  const parseConditionRule = (rule) => {
    if (!rule) return null;
    if (typeof rule === 'string') {
      try { return JSON.parse(rule); } catch { return null; }
    }
    return rule;
  };

  // Check if a value is "set" (not null, undefined, empty string, or empty array)
  const hasValue = (value) => {
    if (value === null || value === undefined || value === '' || value === 'null') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    return true;
  };

  // Compute disabled params based on condition_rule (mutually exclusive)
  const disabledParams = useMemo(() => {
    if (!item?.parameters) return {};
    
    const disabled = {}; // { paramName: { reason: string, conflictsWith: string } }
    
    item.parameters.forEach(param => {
      const rule = parseConditionRule(param.condition_rule);
      if (!rule) return;
      
      const paramName = param.parameter_name;
      const paramValue = textInputs?.[paramName] || uploadedImages?.[paramName];
      const paramHasValue = hasValue(paramValue);
      
      // Handle mutually_exclusive_with
      if (rule.mutually_exclusive_with && paramHasValue) {
        rule.mutually_exclusive_with.forEach(exclusiveParam => {
          disabled[exclusiveParam] = {
            reason: `Cannot be used with "${param.parameter_title || paramName}"`,
            conflictsWith: paramName
          };
        });
      }
    });
    
    return disabled;
  }, [item, textInputs, uploadedImages]);

  // Count additional parameters for settings badge
  const additionalParamsCount = useMemo(() => {
    if (!item?.parameters) return 0;
    
    const handledParams = new Set([
      ...textParams.map(p => p.parameter_name),
      ...selectParams.map(p => p.parameter_name),
      ...imageParams.map(p => p.paramName),
      'width', 'height', 'steps', 'num_inference_steps', 'cfg_scale', 'guidance_scale',
      'sampler', 'scheduler', 'seed', 'num_images', 'num_outputs', 'batch_size'
    ]);
    
    return item.parameters.filter(p => 
      !handledParams.has(p.parameter_name) && !p.is_private
    ).length;
  }, [item, textParams, selectParams, imageParams]);

  // Get images for a param
  const getImagesForParam = (paramName) => {
    if (!uploadedImages || typeof uploadedImages !== 'object') return [];
    const val = uploadedImages[paramName];
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return [val];
  };

  // Handle media upload (image / video / audio)
  const handleImageUpload = async (param, file) => {
    if (!file) return;

    const kind = param?.kind || 'image';
    setIsUploading(true);

    try {
      let uploadFile = file;
      // Compression is image-only — running it on a video/audio file wastes
      // CPU and hands browser-image-compression a fileType it cannot honour.
      if (kind === 'image') {
        const options = {
          maxSizeMB: 10,
          maxWidthOrHeight: undefined,
          useWebWorker: true,
          fileType: file.type,
          initialQuality: 1,
          alwaysKeepResolution: true,
        };
        try {
          uploadFile = await imageCompression(file, options);
        } catch (e) {
          uploadFile = file;
        }
      }

      const { url } = await uploadMediaFile(uploadFile, kind);
      if (url) {
        handleAddImageToParam(url, param);
      }
    } catch (err) {
      // Surface it. Video is capped at 100 MB and audio at 25 MB with a narrow
      // mime list, so 400/413 responses are now realistic — a console-only log
      // left the user staring at a spinner that stopped for no visible reason.
      // uploadMediaFile already normalizes the uro-api body into err.message.
      const msg = err?.message || 'Upload failed';
      console.error('Media upload failed:', err);
      setUploadError(msg);
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  // Add image to param
  const handleAddImageToParam = (url, param) => {
    setUploadedImages(prev => {
      const current = prev || {};
      const currentImages = current[param.paramName];
      
      if (param.type === 'single') {
        return { ...current, [param.paramName]: url };
      } else {
        const arr = Array.isArray(currentImages) ? currentImages : [];
        if (arr.length < param.max) {
          return { ...current, [param.paramName]: [...arr, url] };
        }
        return current;
      }
    });
  };

  // Remove image from param
  const handleRemoveImageFromParam = (paramName, index) => {
    setUploadedImages(prev => {
      if (!prev) return prev;
      const current = { ...prev };
      const val = current[paramName];
      
      if (Array.isArray(val)) {
        const newArr = [...val];
        newArr.splice(index, 1);
        current[paramName] = newArr.length > 0 ? newArr : undefined;
      } else {
        current[paramName] = undefined;
      }
      
      const hasAny = Object.values(current).some(v => v !== undefined);
      return hasAny ? current : null;
    });
  };

  // Build params for generation (same logic as ControlBar)
  const buildParams = useCallback(() => {
    const params = {};
    const availableParams = item?.parameters || [];
    const paramNames = availableParams.map(p => p.parameter_name);
    
    // Apply defaults
    availableParams.forEach(param => {
      if (param.is_private || Number(param.is_private) === 1) return;
      
      if (param.default_value !== undefined && param.default_value !== null && param.default_value !== '') {
        params[param.parameter_name] = param.default_value;
      } else if (param.allowed_values) {
        let values = param.allowed_values;
        if (typeof values === 'string') {
          try { 
            const parsed = JSON.parse(values);
            values = Array.isArray(parsed) ? parsed : [parsed];
          } catch { 
            values = values.split(',').map(v => v.trim()); 
          }
        }
        if (Array.isArray(values) && values.length > 0) {
          params[param.parameter_name] = values[0];
        }
      }
    });
    
    // Add text inputs
    if (textInputs && typeof textInputs === 'object') {
      Object.entries(textInputs).forEach(([paramName, value]) => {
        if (value && paramNames.includes(paramName)) {
          params[paramName] = typeof value === 'string' ? value.trim() : value;
        }
      });
    }
    
    // Add images
    if (uploadedImages && typeof uploadedImages === 'object') {
      Object.entries(uploadedImages).forEach(([paramName, value]) => {
        if (value && paramNames.includes(paramName)) {
          params[paramName] = value;
        }
      });
    }
    
    // Add dimensions if set
    if (settings.width && settings.height) {
      if (paramNames.includes('width')) params.width = settings.width;
      if (paramNames.includes('height')) params.height = settings.height;
    }
    
    // Add num_images
    if (paramNames.includes('num_images')) {
      params.num_images = settings.num_images;
    }
    
    return params;
  }, [item?.parameters, textInputs, uploadedImages, settings]);

  // Check if can generate
  const canGenerate = useMemo(() => {
    // Check required text params
    const requiredTextParams = textParams.filter(p => Number(p.is_required) === 1 || p.is_required === true);
    const hasRequiredText = requiredTextParams.length === 0 || 
      requiredTextParams.every(p => textInputs?.[p.parameter_name]?.trim?.());
    
    // Check required images
    const requiredImageParams = imageParams.filter(p => p.required);
    const hasRequiredImages = requiredImageParams.length === 0 ||
      requiredImageParams.every(p => getImagesForParam(p.paramName).length > 0);
    
    return hasRequiredText && hasRequiredImages;
  }, [textParams, imageParams, textInputs, uploadedImages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle generate click
  const handleGenerateClick = useCallback(() => {
    if (generating || isUploading || !canGenerate) return;
    const params = buildParams();
    onGenerate(params);
  }, [generating, isUploading, canGenerate, buildParams, onGenerate]);

  // Memoized handlers for child components
  const handleSettingsChange = useCallback((updates) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  const handleTextInputChange = useCallback((paramName, value) => {
    setTextInputs(prev => ({ ...prev, [paramName]: value }));
  }, []);

  const handleAdvancedSettingsOpen = useCallback(() => {
    setAdvancedSettingsOpen(true);
  }, []);

  const handleNumImagesChange = useCallback((value) => {
    setSettings(prev => ({ ...prev, num_images: value }));
  }, []);

  // Generating state with enhanced progress UI
  if (generating) {
    const steps = [
      { label: 'Preparing request', status: jobStatus === 'pending' ? 'active' : 'done' },
      { label: 'Processing with AI', status: jobStatus === 'processing' ? 'active' : (jobStatus === 'pending' ? 'pending' : 'done') },
      { label: 'Finalizing output', status: jobStatus === 'completed' ? 'done' : 'pending' },
    ];
    
    return (
      <div style={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.background,
        padding: 20,
      }}>
        {/* Animated glow ring */}
        <div
          style={{
            position: 'relative',
            width: 120,
            height: 120,
            marginBottom: 32,
          }}
        >
          {/* Outer glow */}
          <div style={{
            position: 'absolute',
            inset: -10,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${PRIMARY}20 0%, transparent 70%)`,
            animation: 'pulse 2s ease-in-out infinite',
          }} />
          {/* Ring */}
          <svg width="120" height="120" viewBox="0 0 120 120" style={{ animation: 'spin 3s linear infinite' }}>
            <defs>
              <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={PRIMARY} />
                <stop offset="100%" stopColor="#ff6b00" />
              </linearGradient>
            </defs>
            <circle cx="60" cy="60" r="54" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="4" />
            <circle cx="60" cy="60" r="54" fill="none" stroke="url(#grad)" strokeWidth="4" strokeLinecap="round" strokeDasharray="200 340" />
          </svg>
          {/* Center icon */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 48,
            height: 48,
            borderRadius: 24,
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #ff6b00 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 8px 32px ${PRIMARY}40`,
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>
        </div>
        
        {/* Title */}
        <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 8, textAlign: 'center' }}>
          Creating your masterpiece
        </div>
        <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 32, textAlign: 'center' }}>
          Estimated time: ~{item?.averageEta || 30}s
        </div>
        
        {/* Progress steps */}
        <div style={{ width: '100%', maxWidth: 280 }}>
          {steps.map((step, idx) => (
            <div key={idx} style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 12,
              marginBottom: idx < steps.length - 1 ? 16 : 0,
              opacity: step.status === 'pending' ? 0.4 : 1,
              transition: 'opacity 0.3s ease',
            }}>
              {/* Step indicator */}
              <div style={{
                width: 28,
                height: 28,
                borderRadius: 14,
                background: step.status === 'done' ? PRIMARY : (step.status === 'active' ? `${PRIMARY}30` : 'rgba(255,255,255,0.08)'),
                border: step.status === 'active' ? `2px solid ${PRIMARY}` : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}>
                {step.status === 'done' ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : step.status === 'active' ? (
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: 5,
                    background: PRIMARY,
                    animation: 'pulse 1s ease-in-out infinite',
                  }} />
                ) : (
                  <div style={{ width: 8, height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.3)' }} />
                )}
              </div>
              {/* Step label */}
              <span style={{ 
                fontSize: 14, 
                color: step.status === 'active' ? '#fff' : (step.status === 'done' ? '#9ca3af' : '#6b7280'),
                fontWeight: step.status === 'active' ? 500 : 400,
              }}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
        
        <style>{`
          @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.05); } }
        `}</style>
      </div>
    );
  }

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: colors.background,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          flexShrink: 0,
        }}
      >
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>Create Image</span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: 8,
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      {/* Scrollable Content */}
      <div
        className="hide-scrollbar"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: 16,
        }}
      >
        {/* Cover Image/Video */}
        {(item?.videoUrl || item?.coverImage) && (
          <div
            style={{
              position: 'relative',
              borderRadius: 16,
              overflow: 'hidden',
              marginBottom: 16,
              aspectRatio: '16 / 9',
            }}
          >
            <ContentMedia
              videoUrl={item.videoUrl}
              coverImage={item.coverImage}
              cover2Image={item.cover2Image}
              thumbnailImage={item.thumbnailImage}
            />
            {/* Title overlay */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                padding: '40px 16px 16px',
                background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
              }}
            >
              <div style={{ 
                fontSize: 24, 
                fontWeight: 700, 
                color: PRIMARY,
                textTransform: 'uppercase',
                letterSpacing: 1,
              }}>
                {item.name}
              </div>
            </div>
            {/* Top right controls */}
            <div
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: 8,
              }}
            >
              {/* Change Model button */}
              <button
                onClick={onChangeModel}
                style={{
                  padding: '6px 12px',
                  background: 'rgba(0,0,0,0.6)',
                  backdropFilter: 'blur(8px)',
                  border: 'none',
                  borderRadius: 20,
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                </svg>
                Change Model
              </button>
              {/* Cost estimate under Change button */}
              {item?.averageCost != null && (
                <div
                  style={{
                    padding: '4px 10px',
                    background: 'rgba(0,0,0,0.6)',
                    backdropFilter: 'blur(8px)',
                    borderRadius: 12,
                    fontSize: 11,
                    color: '#fff',
                  }}
                >
                  ~{(Number(item.averageCost) * settings.num_images).toFixed(4)} URO
                </div>
              )}
            </div>
          </div>
        )}

        {/* Warning Message */}
        {item?.warningMessage && (
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: 12,
              background: 'rgba(250, 139, 20, 0.1)',
              border: '1px solid rgba(250, 139, 20, 0.3)',
              borderRadius: 12,
              marginBottom: 16,
            }}
          >
            <div style={{ 
              width: 20, 
              height: 20, 
              borderRadius: '50%', 
              background: PRIMARY, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <span style={{ color: '#000', fontSize: 12, fontWeight: 700 }}>!</span>
            </div>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', lineHeight: 1.4 }}>
              {item.warningMessage}
            </div>
          </div>
        )}


        {/* Image Upload Section */}
        <ImageUploadSection
          imageParams={imageParams}
          getImagesForParam={getImagesForParam}
          handleImageUpload={handleImageUpload}
          handleRemoveImageFromParam={handleRemoveImageFromParam}
          disabledParams={disabledParams}
          isUploading={isUploading}
          uploadError={uploadError}
          fileInputRefs={fileInputRefs}
        />

        {/* Text Parameters (Prompts) */}
        {textParams.map((param, paramIdx) => {
          const charCount = (textInputs[param.parameter_name] || '').length;
          const isOverLimit = charCount > CHAR_LIMIT;
          const isWarning = charCount > CHAR_WARNING && charCount <= CHAR_LIMIT;
          const paramName = param.parameter_name.toLowerCase();
          const isEmpty = !textInputs[param.parameter_name];
          
          // Determine prompt suggestions based on parameter type and item
          const getSuggestions = () => {
            const isPrompt = paramName.includes('prompt') || paramName.includes('text') || paramName.includes('description');
            const isNegative = paramName.includes('negative');
            
            if (isNegative) {
              return [
                'blurry, low quality',
                'watermark, text, logo',
                'distorted, deformed',
              ];
            }
            
            if (isPrompt && paramIdx === 0) {
              // Main prompt suggestions based on item type
              const itemName = item?.name?.toLowerCase() || '';
              if (itemName.includes('portrait') || itemName.includes('face')) {
                return [
                  'professional headshot, studio lighting',
                  'cinematic portrait, dramatic lighting',
                  'natural outdoor portrait, golden hour',
                ];
              }
              if (itemName.includes('product') || itemName.includes('photo')) {
                return [
                  'minimalist product shot, white background',
                  'lifestyle product photography, natural setting',
                  'studio product shot, professional lighting',
                ];
              }
              if (itemName.includes('video') || itemName.includes('animate')) {
                return [
                  'smooth camera movement, cinematic',
                  'dynamic motion, energetic feel',
                  'gentle zoom, slow motion effect',
                ];
              }
              // Generic suggestions
              return [
                'high quality, detailed, professional',
                'cinematic lighting, dramatic mood',
                'vibrant colors, sharp focus',
              ];
            }
            
            return [];
          };
          
          const suggestions = getSuggestions();
          
          return (
            <div key={param.parameter_name} style={{ marginBottom: 16 }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8 
              }}>
                <label style={{ 
                  fontSize: 13, 
                  fontWeight: 500, 
                  color: '#fff', 
                }}>
                  {formatParamLabel(param.parameter_name, param.parameter_title)}
                  {(param.is_required || Number(param.is_required) === 1) && (
                    <span style={{ color: '#ef4444', marginLeft: 4 }}>*</span>
                  )}
                </label>
                <span style={{ 
                  fontSize: 11, 
                  color: isOverLimit ? '#ef4444' : isWarning ? '#f59e0b' : '#6b7280',
                  fontWeight: isOverLimit || isWarning ? 500 : 400,
                }}>
                  {charCount}/{CHAR_LIMIT}
                </span>
              </div>
              <textarea
                value={textInputs[param.parameter_name] || ''}
                onChange={(e) => setTextInputs(prev => ({ ...prev, [param.parameter_name]: e.target.value }))}
                placeholder={param.description || `Type your ${formatParamLabel(param.parameter_name, param.parameter_title).toLowerCase()} here...`}
                rows={4}
                maxLength={CHAR_LIMIT + 100}
                style={{
                  width: '100%',
                  padding: 14,
                  background: 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isOverLimit ? '#ef4444' : isWarning ? 'rgba(245, 158, 11, 0.5)' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 15,
                  resize: 'vertical',
                  minHeight: 100,
                }}
              />
              
              {/* Prompt Suggestions - shown when empty */}
              {suggestions.length > 0 && isEmpty && (
                <div style={{ marginTop: 8 }}>
                  <div style={{ 
                    fontSize: 11, 
                    color: '#6b7280', 
                    marginBottom: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4,
                  }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                    </svg>
                    Quick suggestions
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => setTextInputs(prev => ({ 
                          ...prev, 
                          [param.parameter_name]: prev[param.parameter_name] 
                            ? `${prev[param.parameter_name]}, ${suggestion}`
                            : suggestion
                        }))}
                        style={{
                          padding: '6px 10px',
                          background: 'rgba(255,255,255,0.04)',
                          border: '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 16,
                          color: '#9ca3af',
                          fontSize: 11,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = `${PRIMARY}15`;
                          e.currentTarget.style.borderColor = `${PRIMARY}40`;
                          e.currentTarget.style.color = PRIMARY;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                          e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                          e.currentTarget.style.color = '#9ca3af';
                        }}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}


        {/* Settings Row */}
        <SettingsRow
          dimensionConfig={dimensionConfig}
          settings={settings}
          onSettingsChange={handleSettingsChange}
          selectParams={selectParams}
          textInputs={textInputs}
          onTextInputChange={handleTextInputChange}
          additionalParamsCount={additionalParamsCount}
          onAdvancedSettingsOpen={handleAdvancedSettingsOpen}
          formatParamLabel={formatParamLabel}
        />
      </div>

      {/* Generate Footer */}
      <GenerateFooter
        numImages={settings.num_images}
        maxImages={4}
        onNumImagesChange={handleNumImagesChange}
        onGenerate={handleGenerateClick}
        generating={generating}
        isUploading={isUploading}
        canGenerate={canGenerate}
      />

      {/* Advanced Settings Modal */}
      {advancedSettingsOpen && (
        <AdvancedSettingsModal
          open={advancedSettingsOpen}
          onClose={() => setAdvancedSettingsOpen(false)}
          item={item}
          textInputs={textInputs}
          setTextInputs={setTextInputs}
          settings={settings}
          setSettings={setSettings}
          textParams={textParams}
          selectParams={selectParams}
          imageParams={imageParams}
          formatParamLabel={formatParamLabel}
        />
      )}
    </div>
  );
}
